import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const planned = [
  {
    category: 'Applicant Tracking Systems',
    items: [
      { name: 'Greenhouse', logo: '🌱', status: 'Planned Q3 2026' },
      { name: 'Lever', logo: '⚡', status: 'Planned Q3 2026' },
      { name: 'Workday Recruiting', logo: '☁️', status: 'Planned Q4 2026' },
      { name: 'SAP SuccessFactors', logo: '🔷', status: 'Planned Q4 2026' },
      { name: 'iCIMS', logo: '🎯', status: 'Planned 2027' },
      { name: 'SmartRecruiters', logo: '🧠', status: 'Planned 2027' },
    ]
  },
  {
    category: 'HRIS & HR Platforms',
    items: [
      { name: 'BambooHR', logo: '🎋', status: 'Planned Q4 2026' },
      { name: 'Workday HCM', logo: '☁️', status: 'Planned Q4 2026' },
      { name: 'ADP Workforce Now', logo: '🏢', status: 'Planned 2027' },
      { name: 'UKG Pro', logo: '🔵', status: 'Planned 2027' },
    ]
  },
  {
    category: 'Communication & Collaboration',
    items: [
      { name: 'Microsoft Teams', logo: '💼', status: 'Planned Q3 2026' },
      { name: 'Slack', logo: '💬', status: 'Planned Q3 2026' },
      { name: 'Zoom', logo: '📹', status: 'Planned Q4 2026' },
      { name: 'Google Meet', logo: '📞', status: 'Planned Q4 2026' },
    ]
  },
  {
    category: 'Calendar & Scheduling',
    items: [
      { name: 'Google Calendar', logo: '📅', status: 'Planned Q3 2026' },
      { name: 'Microsoft Outlook', logo: '📧', status: 'Planned Q3 2026' },
      { name: 'Calendly', logo: '🗓️', status: 'Planned Q4 2026' },
    ]
  },
];

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [integration, setIntegration] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    // Simple email capture — can wire to backend later
    await new Promise(r => setTimeout(r, 800));
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Quali5d Integrations — Connect Your ATS, HRIS, and Calendar</title>
        <meta name="description" content="Native integrations with Greenhouse, Lever, Workday, BambooHR, Slack, Teams, and more. Plus REST API for custom workflows on Enterprise plans." />
        <link rel="canonical" href="https://www.quali5d.com/integrations" />
        <meta property="og:title" content="Quali5d Integrations — Connect Your ATS, HRIS, and Calendar" />
        <meta property="og:description" content="Native integrations with Greenhouse, Lever, Workday, BambooHR, Slack, Teams, and more. Plus REST API for custom workflows on Enterprise plans." />
        <meta property="og:url" content="https://www.quali5d.com/integrations" />
        <meta name="twitter:title" content="Quali5d Integrations — Connect Your ATS, HRIS, and Calendar" />
        <meta name="twitter:description" content="Native integrations with Greenhouse, Lever, Workday, BambooHR, Slack, Teams, and more. Plus REST API for custom workflows on Enterprise plans." />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span className="text-sm font-medium">Back to Quali5d</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">Q5</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Quali5d</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-1.5 mb-5">
            <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></span>
            <span className="text-xs font-semibold text-orange-700">Coming Soon</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-4">Integrations</h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Quali5d is building native integrations with the tools your team already uses.
            Connect your ATS, HRIS, and communication platforms directly.
          </p>
        </div>

        {/* Notify me form */}
        <div className="bg-gray-900 rounded-2xl p-8 mb-12 text-center">
          <h2 className="text-white text-xl font-bold mb-2">Get notified when integrations launch</h2>
          <p className="text-gray-400 text-sm mb-6">Tell us which integration you need most and we'll notify you the moment it's ready.</p>
          {submitted ? (
            <div className="flex items-center justify-center gap-2 text-green-400 font-semibold">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              You're on the list — we'll notify you when it launches!
            </div>
          ) : (
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@company.com"
                required
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 text-white placeholder-gray-500 border border-white/20 focus:outline-none focus:border-white/40 text-sm"
              />
              <input
                type="text"
                value={integration}
                onChange={e => setIntegration(e.target.value)}
                placeholder="Which integration? (e.g. Greenhouse)"
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 text-white placeholder-gray-500 border border-white/20 focus:outline-none focus:border-white/40 text-sm"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors text-sm flex-shrink-0 disabled:opacity-50"
              >
                {submitting ? 'Saving…' : 'Notify Me'}
              </button>
            </form>
          )}
        </div>

        {/* Planned integrations */}
        <div className="space-y-10">
          {planned.map(group => (
            <div key={group.category}>
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-blue-500 rounded-full"></span>
                {group.category}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {group.items.map(item => (
                  <div key={item.name} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center text-center">
                    <span className="text-3xl mb-2">{item.logo}</span>
                    <div className="text-sm font-semibold text-gray-800 mb-1">{item.name}</div>
                    <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{item.status}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* API note */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
          <h3 className="font-bold text-blue-900 mb-2">Need a custom integration now?</h3>
          <p className="text-sm text-blue-700 mb-4">
            Our REST API is available on Enterprise plans. Build your own integration with any system you use.
          </p>
          <a href="mailto:admin@uni5d.ai?subject=API Integration Enquiry"
            className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
            Contact us about API access →
          </a>
        </div>

        <div className="mt-8 text-center text-sm text-gray-400">
          © 2026 Uni5d Technology Solutions LLC · Questions? <a href="mailto:admin@uni5d.ai" className="text-blue-600 hover:underline">admin@uni5d.ai</a>
        </div>
      </div>
    </div>
  );
}